// Which video files this browser gets. Written by tools/encode_media.py into public/media/media.json.

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MediaMeta {
  source: { file: string; width: number; height: number };
  journey_end: number;
  loop: { t0: number; length: number; crossfade: number };
  crt: Rect;
  close: Rect;
  limit_bytes?: number;
  /** files served from the large-media host instead of the site's own assets */
  large?: string[];
  files: Record<string, { bytes: number; width?: number; height?: number; duration?: number }>;
}

export interface Choice {
  journey: string;
  loop: string;
  close: string;
  /** e.g. "AV1 1440p60" for the status line and the logs */
  label: string;
}

const BASE = "./media/";
/**
 * The large journey films (marked `large` in media.json) come from /media/large/: in production
 * the Worker joins them from parts under 25 MiB, in development Vite serves them from media-large/.
 */
const LARGE_BASE = "./media/large/";

interface Variant {
  label: string;
  journey: string;
  loop: string;
  close: string;
  /** container + codecs, for canPlayType */
  mime: string;
  /** one video track, for mediaCapabilities */
  video: string;
  audio: { contentType: string; bitrate: number };
  width: number;
  height: number;
}

const VARIANTS: Variant[] = [
  {
    label: "AV1 1440p60", journey: "intro-1440.av1.webm", loop: "loop-1440.av1.webm", close: "close-1080.av1.webm",
    mime: 'video/webm; codecs="av01.0.12M.08, opus"', video: 'video/webm; codecs="av01.0.12M.08"',
    audio: { contentType: 'audio/webm; codecs="opus"', bitrate: 128_000 }, width: 2560, height: 1440,
  },
  {
    label: "AV1 1080p60", journey: "intro-1080.av1.webm", loop: "loop-1080.av1.webm", close: "close-1080.av1.webm",
    mime: 'video/webm; codecs="av01.0.09M.08, opus"', video: 'video/webm; codecs="av01.0.09M.08"',
    audio: { contentType: 'audio/webm; codecs="opus"', bitrate: 128_000 }, width: 1920, height: 1080,
  },
  {
    label: "H.264 1080p60", journey: "intro-1080.h264.mp4", loop: "loop-1080.h264.mp4", close: "close-1080.h264.mp4",
    mime: 'video/mp4; codecs="avc1.64002A, mp4a.40.2"', video: 'video/mp4; codecs="avc1.64002A"',
    audio: { contentType: 'audio/mp4; codecs="mp4a.40.2"', bitrate: 160_000 }, width: 1920, height: 1080,
  },
];

export async function loadMeta(): Promise<MediaMeta> {
  const r = await fetch(BASE + "media.json", { cache: "no-cache" });
  if (!r.ok) throw new Error(`media.json: HTTP ${r.status}`);
  return (await r.json()) as MediaMeta;
}

/** Average bitrate of the file as encoded (video + audio), from media.json. */
function bitrateOf(meta: MediaMeta, file: string): number {
  const f = meta.files[file];
  return f?.bytes && f.duration ? Math.round((f.bytes * 8) / f.duration) : 10_000_000;
}

/** Can this browser play the variant, and decode it smoothly at 60 fps? */
async function playable(v: Variant, meta: MediaMeta, needSmooth: boolean): Promise<boolean> {
  const video = document.createElement("video");
  const can = video.canPlayType(v.mime);
  if (can === "") return false;
  const mc = navigator.mediaCapabilities;
  if (!mc?.decodingInfo) return can === "probably";
  try {
    const total = bitrateOf(meta, v.journey);
    const info = await mc.decodingInfo({
      type: "file",
      video: { contentType: v.video, width: v.width, height: v.height, bitrate: Math.max(1, total - v.audio.bitrate), framerate: 60000 / 1001 },
      audio: { contentType: v.audio.contentType, channels: "2", bitrate: v.audio.bitrate, samplerate: 48000 },
    });
    return info.supported && (!needSmooth || info.smooth);
  } catch {
    return can === "probably";
  }
}

/** Physical pixels across the screen the window is on. */
function physicalWidth(): number {
  return Math.round(window.screen.width * (window.devicePixelRatio || 1));
}

/** Pick the best variant that decodes smoothly; failing that, any supported one; else throw. */
export async function choose(meta: MediaMeta): Promise<Choice> {
  const big = physicalWidth() >= 2560;
  const candidates = VARIANTS.filter((v) => v.journey in meta.files && (big || v.height <= 1080));
  for (const needSmooth of [true, false]) {
    for (const v of candidates) {
      if (await playable(v, meta, needSmooth)) {
        const jBase = meta.large?.includes(v.journey) ? LARGE_BASE : BASE;
        return { journey: jBase + v.journey, loop: BASE + v.loop, close: BASE + v.close, label: v.label };
      }
    }
  }
  throw new Error("no playable video format");
}

export function sizeOf(meta: MediaMeta, url: string): number {
  return meta.files[url.slice(url.lastIndexOf("/") + 1)]?.bytes ?? 0;
}
